import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "../../utils/axios";
import Sidebar from "./sidebar";
import { getUser } from "../../utils/auth";
import resolveImageUrl from "../../utils/imageUtils";
import toast, { Toaster } from "react-hot-toast";
import { getPropertyUpdateErrorMessage } from "../../utils/propertyErrors";
import {
  hasInspectorFinalDecision,
  isSupervisorDecisionLocked,
} from "../../utils/flaggedSupervisorUtils";
import { 
  MapPin, 
  Home, 
  Layers, 
  Navigation, 
  Ruler, 
  Building,
  CheckCircle,
  XCircle,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  X,
  Save,
  Edit,
  RotateCw,
  AlertCircle,
  Info,
  ArrowRightLeft,
  TrendingUp,
  FileText,
  Maximize2
} from "lucide-react";

const ITEMS_PER_PAGE = 8;

const appendCacheBuster = (url, buster) => {
  if (!url || !buster) return url;
  return url.includes("?") ? `${url}&t=${buster}` : `${url}?t=${buster}`;
};

const Assigned = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showFullImage, setShowFullImage] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [savingImageRotation, setSavingImageRotation] = useState(false);
  const [highlightPin, setHighlightPin] = useState("");
  const searchParams = new URLSearchParams(location.search);
  const targetPinFromQuery = searchParams.get("pin")?.trim() || "";
  const [autoOpenedPin, setAutoOpenedPin] = useState("");
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");

  const getLatestSupervisorAssignment = (property) => {
    const assignments = property?.assignments;
    if (!Array.isArray(assignments) || assignments.length === 0) return null;
    return assignments.reduce((latest, current) => {
      if (!latest) return current;
      const latestTime = latest.assigned_at ? new Date(latest.assigned_at).getTime() : 0;
      const currentTime = current.assigned_at ? new Date(current.assigned_at).getTime() : 0;
      return currentTime >= latestTime ? current : latest;
    }, null);
  };

  const handleRotate = (e) => {
    e.stopPropagation();
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSaveRotation = async (e) => {
    e.stopPropagation();
    if (!selectedProperty?.pin || rotation === 0 || savingImageRotation) return;

    const toastId = toast.loading("Saving rotated image...");
    setSavingImageRotation(true);
    
    try {
      const totalRotations = Math.abs(rotation / 90);
      for (let i = 0; i < totalRotations; i++) {
        await axios.post(`/properties/rotate-image-id/${selectedProperty.id}`, { 
          pin: selectedProperty.pin, 
          rotateBy: 90, 
          forcePortrait: false 
        });
      }
      
      const bust = Date.now();
      setProperties((prev) =>
        prev.map((p) => (p.pin === selectedProperty.pin ? { ...p, _imgBust: bust } : p))
      );
      setSelectedProperty((prev) =>
        prev?.pin === selectedProperty.pin ? { ...prev, _imgBust: bust } : prev
      );
      setRotation(0);
      toast.success("Image saved successfully!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save image", {
        id: toastId,
      });
    } finally {
      setSavingImageRotation(false);
    }
  };

  const normalizePin = (pin) => String(pin || "").trim().toLowerCase();
  const dedupeByPin = (list) => {
    const seen = new Set();
    return (list || []).filter((p) => {
      const key = normalizePin(p?.pin);
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const [comment, setComment] = useState("");
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [showNotReassessModal, setShowNotReassessModal] = useState(false);
  const [notReassessComment, setNotReassessComment] = useState("");
  const [features, setFeatures] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [actionType, setActionType] = useState(null); // 'reassess' or 'no-reassess'
  const [showAreaTable, setShowAreaTable] = useState(false);

  const user = getUser();
  const userId = user?.id;

  const hasUsableValue = (value) => {
    if (value === null || value === undefined) return false;
    const str = String(value).trim().toLowerCase();
    return str !== "" && str !== "null" && str !== "undefined";
  };

  const toFiniteNumber = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "number") return Number.isFinite(val) ? val : null;
    const n = Number(String(val).replaceAll(",", "").trim());
    return Number.isFinite(n) ? n : null;
  };

  const formatTwoDecimals = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (!str) return "";
    const lowered = str.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return "";
    const n = toFiniteNumber(str);
    if (n === null) return str;
    return String(Math.round(n));
  };

  const pickFirstValue = (property, keys) => {
    for (const key of keys) {
      const value = property?.[key];
      if (hasUsableValue(value)) return value;
    }
    return "";
  };

  const getMLDisplayValue = (property, field) => {
    let realValue = "";
    switch (field) {
      case 'floor':
        realValue = pickFirstValue(property, ["floors_ml", "floor_ml", "floors_pred", "floor_pred"]);
        break;
      case 'landuse':
        realValue = pickFirstValue(property, ["landuse_ml", "land_use_ml", "land_use_pred", "landuse_pred"]);
        break;
      case 'landarea':
        {
          const ml = pickFirstValue(property, ["landarea_ml", "land_area_ml"]);
          const bucket = pickFirstValue(property, ["landarea_combined_pred"]);
          const n = Number(String(ml ?? "").replaceAll(",", "").trim());
          const mlIsZeroish = Number.isFinite(n) && n <= 0;
          realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        }
        break;
      case 'coveredarea':
        {
          const ml = pickFirstValue(property, ["coveredarea_ml", "covered_area_ml"]);
          const bucket = pickFirstValue(property, ["coveredarea_combined_pred"]);
          const n = Number(String(ml ?? "").replaceAll(",", "").trim());
          const mlIsZeroish = Number.isFinite(n) && n <= 0;
          realValue = hasUsableValue(bucket) && (!hasUsableValue(ml) || mlIsZeroish) ? bucket : (ml || bucket);
        }
        break;
    }

    if (hasUsableValue(realValue)) {
      if (field === "landarea" || field === "coveredarea") {
        // Check if it's a range string first
        const strVal = String(realValue);
        const isRange = strVal.toLowerCase().includes("greater") || 
                        strVal.toLowerCase().includes("less") || 
                        strVal.toLowerCase().includes("between");
        if (isRange) {
          return realValue;
        }
        return formatTwoDecimals(realValue) || "N/A";
      }
      const num = Number(realValue);
      if (Number.isFinite(num) && !isNaN(num)) {
        return Number.isInteger(num) ? num : Math.round(num);
      }
      return realValue;
    }
    return 'N/A';
  };

  const parseRange = (rangeStr) => {
    if (!rangeStr) return null;
    const str = String(rangeStr).toLowerCase().trim();
    
    // Remove any units (sq yards, sq yds, sq ft, sq.yds, sq.ft, etc.)
    const cleanStr = str.replace(/sq\.?\s*(yards?|yds?|ft|feet?)|,/g, '').trim();
    
    // Check for "greater than X" or "more than X"
    let match = cleanStr.match(/(?:greater|more)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'greater', value: Number(match[1]) };
    }
    
    // Check for "less than X" or "under X"
    match = cleanStr.match(/(?:less|under)\s+than\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { type: 'less', value: Number(match[1]) };
    }
    
    // Check for "between X and Y"
    match = cleanStr.match(/between\s+(\d+(?:\.\d+)?)\s+and\s+(\d+(?:\.\d+)?)/);
    if (match) {
      return { 
        type: 'between', 
        min: Number(match[1]), 
        max: Number(match[2]) 
      };
    }
    
    // Try to parse as a single number
    const num = Number(cleanStr);
    if (Number.isFinite(num)) {
      return { type: 'exact', value: num };
    }
    
    return null;
  };

  const areValuesDifferent = (adminVal, mlVal, field) => {
    const normalizeNumber = (val) => {
      if (val === null || val === undefined || val === 'N/A' || val === '') return null;
      const numStr = String(val).replace(/[^0-9.]/g, '');
      const numeric = Number(numStr);
      if (Number.isFinite(numeric)) return numeric;
      return null;
    };
    
    // For landarea and coveredarea, check if admin is within ML range
    if ((field === "landarea" || field === "land_area" || field === "coveredarea" || field === "covered_area") && mlVal) {
      const range = parseRange(mlVal);
      const adminNum = normalizeNumber(adminVal);
      if (range && typeof adminNum === "number") {
        switch (range.type) {
          case "greater":
            return adminNum <= range.value; // highlight if admin is NOT greater
          case "less":
            return adminNum >= range.value; // highlight if admin is NOT less
          case "between":
            return adminNum < range.min || adminNum > range.max; // highlight if outside range
          case "exact":
            return Math.abs(adminNum - range.value) > 1e-9; // highlight if different
        }
      }
    }

    // For all other fields, use original comparison
    const normalize = (val) => {
      if (val === null || val === undefined || val === 'N/A' || val === '') return '';
      return String(val).trim().toLowerCase();
    };
    const normalizedAdmin = normalize(adminVal);
    const normalizedMl = normalize(mlVal);
    return normalizedAdmin !== '' && normalizedMl !== '' && normalizedAdmin !== normalizedMl;
  };

  const fetchProperties = async () => {
    try {
      const user = getUser();
      if (!user || !user.id) {
        setError("Inspector ID not found");
        return [];
      }

      const res = await axios.get(`/inspector/assigned/${user.id}`, { params: { includeAll: true } });
      const rows = dedupeByPin(res.data || []);
      
      // Sort properties: those with supervisor flag comments come first, sorted by date descending
      const sortedRows = [...rows].sort((a, b) => {
        const aHasFlag = hasUsableValue(a.supervisor_flag_comment) || hasUsableValue(a.supervisor_reason) || hasUsableValue(a.reason);
        const bHasFlag = hasUsableValue(b.supervisor_flag_comment) || hasUsableValue(b.supervisor_reason) || hasUsableValue(b.reason);
        
        if (aHasFlag && !bHasFlag) return -1;
        if (!aHasFlag && bHasFlag) return 1;
        
        // If both have flag, sort by most recent flagged/assigned date
        const aDate = a.flagged_at || a.assigned_at || a.created_at;
        const bDate = b.flagged_at || b.assigned_at || b.created_at;
        
        if (aDate && bDate) {
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        }
        return 0;
      });
      
      setProperties(sortedRows);
      return sortedRows;
    } catch (err) {
      console.error(err);
      setError("Failed to load assigned properties");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedProperty = async (propertyId) => {
    const rows = await fetchProperties();
    if (!propertyId) return null;
    const fresh = rows.find((p) => p.id === propertyId) || null;
    if (fresh) setSelectedProperty(fresh);
    return fresh;
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    const pin = selectedProperty?.pin;
    if (!selectedProperty || !pin) {
      setTimeline([]);
      setTimelineError("");
      setTimelineLoading(false);
      return;
    }

    const fetchTimeline = async () => {
      try {
        setTimelineLoading(true);
        setTimelineError("");
        const res = await axios.get(`/properties/timeline/${encodeURIComponent(pin)}`);
        setTimeline(res.data?.timeline || []);
      } catch (err) {
        console.error("Failed to fetch timeline:", err);
        setTimeline([]);
        setTimelineError("Failed to load previous comments.");
      } finally {
        setTimelineLoading(false);
      }
    };

    fetchTimeline();
  }, [selectedProperty]);

  useEffect(() => {
    if (!targetPinFromQuery) return;
    if (!properties.length) return;
    if (autoOpenedPin && autoOpenedPin === targetPinFromQuery) return;

    const normalizedTarget = targetPinFromQuery.toLowerCase();
    const matched = properties.find(
      (p) => String(p.pin || "").trim().toLowerCase() === normalizedTarget
    );
    if (!matched) return;

    const idx = properties.findIndex(
      (p) => String(p.pin || "").trim().toLowerCase() === normalizedTarget
    );
    const targetPage = idx >= 0 ? Math.floor(idx / ITEMS_PER_PAGE) + 1 : 1;
    setPage(targetPage);
    setHighlightPin(normalizePin(matched.pin));
    setSelectedProperty(matched);
    setAutoOpenedPin(targetPinFromQuery);
  }, [targetPinFromQuery, properties, autoOpenedPin]);

  const totalPages = Math.ceil(properties.length / ITEMS_PER_PAGE);
  const paginated = properties.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  function highlightPropertyCard(pin) {
    const normalized = normalizePin(pin);
    if (!normalized) return;

    const idx = properties.findIndex((p) => normalizePin(p.pin) === normalized);
    if (idx >= 0) {
      const targetPage = Math.floor(idx / ITEMS_PER_PAGE) + 1;
      setPage(targetPage);
    }

    setHighlightPin(normalized);
    setTimeout(() => {
      const el = document.querySelector(`[data-pin="${normalized}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  }

  const closeDetailModal = () => {
    const pinToHighlight = selectedProperty?.pin;
    setSelectedProperty(null);
    setRotation(0);
    highlightPropertyCard(pinToHighlight);
    navigate({ search: "" }, { replace: true });
  };

  const setFeaturesForReassess = (p) => {
    const hasPredictions = p.floors_pred || p.land_use_pred || p.landarea_combined_pred || p.coveredarea_combined_pred;
    const hasMLPredictions = p.floors_ml || p.landuse_ml || p.landarea_ml || p.coveredarea_ml;
    
    setFeatures([
      { 
        key: "floor", 
        label: "Floor", 
        before: p.floor_admin || "", 
        ml: p.floors_ml || "",
        after: p.floors_pred ?? "",
        unit: "",
        changed: hasPredictions && p.floor_admin !== p.floors_pred
      },
      { 
        key: "landuse", 
        label: "Land Use", 
        before: p.landuse_admin || "", 
        ml: p.landuse_ml || "",
        after: p.land_use_pred ?? "",
        unit: "",
        changed: hasPredictions && p.landuse_admin !== p.land_use_pred
      },
      { 
        key: "landarea", 
        label: "Land Area", 
        before: p.landarea_admin || "", 
        ml: p.landarea_ml || "",
        after: p.landarea_combined_pred ?? "",
        unit: "sq.yards",
        changed: hasPredictions && p.landarea_admin !== p.landarea_combined_pred
      },
      { 
        key: "coveredarea", 
        label: "Covered Area", 
        before: p.coveredarea_admin || "", 
        ml: p.coveredarea_ml || "",
        after: p.coveredarea_combined_pred ?? "",
        unit: "sq.ft",
        changed: hasPredictions && p.coveredarea_admin !== p.coveredarea_combined_pred
      },
      { 
        key: "pt", 
        label: "PT-5 Serial Number", 
        before: p.refno || " ", 
        ml: "",
        after: p.refno || "",
        unit: ""
      },
    ]);
  };

  const updateFeature = (index, field, value) => {
    setFeatures((prev) => {
      const updated = [...prev];
      const f = updated[index];
      
      // Validate based on field type
      let validatedValue = value;
      if (field === "after") {
        switch (f.key) {
          case "floor":
          case "landarea":
          case "coveredarea":
            // Should be a number (allow only digits, optional decimal)
            if (/[^0-9.]/.test(value)) {
              toast.error("Please enter only numbers for this field!");
              validatedValue = value.replace(/[^0-9.]/g, "");
            }
            // Prevent multiple decimals
            if ((validatedValue.match(/\./g) || []).length > 1) {
              validatedValue = validatedValue.slice(0, -1);
            }
            // Validate area ranges
            if (validatedValue) {
              const numVal = parseFloat(validatedValue);
              if (f.key === "landarea") {
                // Land Area (sq yards) ranges: 0-31, 32-91, 92-175, 176-300, 301-108000
                if (numVal < 0 || numVal > 108000) {
                  toast.error("Land Area must be between 0 and 108,000 sq yards!");
                }
              } else if (f.key === "coveredarea") {
                // Covered Area (sq ft) ranges: 0-400, 401-977, 978-1710, 1711-2920, 2921-272000
                if (numVal < 0 || numVal > 272000) {
                  toast.error("Covered Area must be between 0 and 272,000 sq ft!");
                }
              }
            }
            break;
          case "landuse":
            // Should be text only (no numbers)
            if (/[0-9]/.test(value)) {
              toast.error("Please enter only letters for Land Use!");
              validatedValue = value.replace(/[0-9]/g, "");
            }
            break;
        }
      }
      
      updated[index][field] = validatedValue;
      // Mark as changed if after value differs from before
      if (field === 'after') {
        updated[index].changed = validatedValue !== updated[index].before;
      }
      return updated;
    });
  };

  const saveComment = async (commentText, propertyId) => {
    if (!commentText.trim()) return null;
    
    try {
      await axios.post("/properties/add-comment", {
        user_id: userId,
        property_id: propertyId,
        comment: commentText.trim(),
      });
      return true;
    } catch (err) {
      console.error("Failed to save comment:", err);
      return false;
    }
  };

  const handleSaveCommentOnly = async () => {
    if (!comment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    try {
      await saveComment(comment, selectedProperty.id);
      toast.success("Comment saved successfully");
      setComment("");
    } catch {
      toast.error("Failed to save comment");
    }
  };

  const handleNoReassess = async () => {
    if (!notReassessComment.trim()) return;
    try {
      setSubmitting(true);
      setActionType('no-reassess');

      const finalComment = notReassessComment.trim();

      const fd = new FormData();

      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "0");

      // Send ALL required admin fields (if exist)
      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");

      fd.append("comment", finalComment);
      fd.append("inspector_comment", finalComment);

      if (userId && selectedProperty?.id) {
        try {
          await axios.post("/inspector/save-explanation", {
            property_id: selectedProperty.id,
            user_id: userId,
            overall_explanation: finalComment,
            row_explanations: {
              floor: "",
              landuse: "",
              landarea: "",
              coveredarea: "",
            },
          });
        } catch (err) {
          console.error("Failed to save explanation:", err);
        }
      }

      // First save to comment API
      const commentSaved = await saveComment(finalComment, selectedProperty.id);
      
      if (!commentSaved) {
        toast.error("Failed to save comment, but proceeding with update...");
      }

      // Then update property status
      await axios.put(`/properties/${selectedProperty.pin}`, fd);

      toast.success("Successfully marked as 'Do Not Reassess'");
      
      setShowReassessModal(false);
      setShowNotReassessModal(false);
      setNotReassessComment("");
      setActionType(null);
      await refreshSelectedProperty(selectedProperty.id);

    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReassess = async () => {
    try {
      setSubmitting(true);
      setActionType('reassess');

      // Prepare final comment - add user's comment if exists, otherwise use default
      let finalComment = comment.trim();
      if (!finalComment) {
        finalComment = "Property reassessed with updated features";
      } else {
        finalComment = `${finalComment} (Property reassessed)`;
      }

      const fd = new FormData();

      fd.append("pin", selectedProperty.pin);
      fd.append("userId", userId);
      fd.append("reassess", "1");

      // Required admin values (existing)
      fd.append("circle", selectedProperty.circle || "");
      fd.append("occupationstatus", selectedProperty.occupationstatus || "");
      fd.append("location", selectedProperty.location || "");
      fd.append("floor_admin", selectedProperty.floor_admin || "");
      fd.append("landuse_admin", selectedProperty.landuse_admin || "");
      fd.append("landarea_admin", selectedProperty.landarea_admin || "");
      fd.append("coveredarea_admin", selectedProperty.coveredarea_admin || "");
      fd.append("admin_garv", selectedProperty.admin_garv || "");

      // Updated prediction values
      features.forEach((f) => {
        const val = f.after?.toString().trim();
        if (!val) return;

        switch (f.key) {
          case "floor":
            fd.append("floors_pred", val);
            break;
          case "landuse":
            fd.append("land_use_pred", val);
            break;
          case "landarea":
            fd.append("landarea_combined_pred", val);
            break;
          case "coveredarea":
            fd.append("coveredarea_combined_pred", val);
            break;
          case "pt":
            fd.append("refno", val);
            break;
        }
      });

      // exp_garv should also be included if it exists
      if (selectedProperty.exp_garv)
        fd.append("exp_garv", selectedProperty.exp_garv);

      // Add comment to the main update
      fd.append("inspector_comment", finalComment);

      // First save to comment API
      const commentSaved = await saveComment(finalComment, selectedProperty.id);
      
      if (!commentSaved) {
        toast.error("Failed to save comment, but proceeding with reassessment...");
      }

      // Then update property reassessment
      await axios.put(`/properties/${selectedProperty.pin}`, fd);

      toast.success("Reassessment saved successfully!");
      
      setShowReassessModal(false);
      setComment("");
      setFeatures([]);
      setActionType(null);
      await refreshSelectedProperty(selectedProperty.id);

    } catch (err) {
      console.error(err);
      toast.error(getPropertyUpdateErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReassessAction = (type) => {
    setActionType(type);
    
    if (type === 'no-reassess') {
      setNotReassessComment(comment);
      setShowNotReassessModal(true);
    } else if (type === 'reassess') {
      // Show reassess modal with features table
      setFeaturesForReassess(selectedProperty);
      setShowReassessModal(true);
    }
  };

  // Check if property has existing predictions
  const hasPredictions = (property) => {
    return property.floors_pred || 
           property.land_use_pred || 
           property.landarea_combined_pred || 
           property.coveredarea_combined_pred;
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-6 md:p-8">
        <Toaster position="top-right" />

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Assigned Properties</h1>
              <p className="text-gray-600 mt-2">Review and inspect properties assigned to you</p>
            </div>
            <div className="text-xs sm:text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full border border-gray-200">
              Showing <span className="font-semibold">{paginated.length}</span> of{" "}
              <span className="font-semibold">{properties.length}</span> properties
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading assigned properties...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-800 mb-2">Unable to Load Properties</h3>
            <p className="text-red-600">{error}</p>
            <button 
              onClick={fetchProperties}
              className="mt-4 px-4 py-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && properties.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Home className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Properties Assigned</h3>
            <p className="text-gray-500">You don't have any properties assigned for inspection yet.</p>
          </div>
        )}

        {/* Property Grid */}
        {!loading && paginated.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginated.map((property) => (
                <motion.div
                  key={property.id}
                  data-pin={normalizePin(property.pin)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white shadow hover:shadow-lg cursor-pointer transition-all relative rounded-xl ${
                    normalizePin(property.pin) && normalizePin(property.pin) === highlightPin
                      ? "border-2 border-sky-500"
                      : "border border-gray-200"
                  }`}
                  onClick={() => setSelectedProperty(property)}
                >
                  {normalizePin(property.pin) && normalizePin(property.pin) === highlightPin && (
                    <div className="absolute top-2 left-2 z-20 w-7 h-7 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-bold shadow">
                      ✓
                    </div>
                  )}
                  <div className="h-48 bg-gray-100 overflow-hidden relative group rounded-t-xl">
                    {property.image ? (
                      <>
                        <img
                          src={appendCacheBuster(resolveImageUrl(property.image), property._imgBust)}
                          alt="Property"
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="bg-white/90 p-3 rounded-full">
                            <Maximize2 className="w-6 h-6 text-gray-800" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-yellow-50 to-blue-50">
                        <span className="text-4xl">🏠</span>
                      </div>
                    )}
                    <div className="absolute top-3 left-3 bg-yellow-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      🚩 Flagged by Supervisor
                    </div>
                    <div className="absolute top-3 right-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          property.reassess == 1 || property.reassess == 0 ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"
                        }`}
                      >
                        {property.reassess == 1 || property.reassess == 0 ? "✓ Responded" : "⏳ Pending"}
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-bold text-lg text-gray-900 mb-2">PIN: {property.pin}</h3>

                    <div className="space-y-2 text-sm text-gray-600">
                      <div>Circle: {property.circle || "N/A"}</div>

                      {(property.reason || property.supervisor_reason || property.supervisor_flag_comment) && (
                        <div className="text-xs text-gray-500 line-clamp-2">
                          Supervisor comment: {(property.supervisor_flag_comment || property.supervisor_reason || property.reason).toString()}
                        </div>
                      )}

                      {(property.inspector_overall_explanation || property.inspector_last_comment) && (
                        <div className="text-xs text-slate-600 line-clamp-2">
                          Your comment:{" "}
                          {(property.inspector_overall_explanation || property.inspector_last_comment).toString()}
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-1 mt-3 py-2 border-y border-gray-100 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Floor (Admin):</span>
                          <span className="font-medium">
                            {hasUsableValue(property.floor_admin) ? property.floor_admin : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Land Use (Admin):</span>
                          <span className="font-medium truncate ml-2 text-right max-w-[160px]">
                            {hasUsableValue(property.landuse_admin) ? property.landuse_admin : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Land Area (Admin):</span>
                          <span className="font-medium">
                            {hasUsableValue(property.landarea_admin)
                              ? `${formatTwoDecimals(property.landarea_admin) || property.landarea_admin} sq.yards`
                              : "N/A"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Covered Area (Admin):</span>
                          <span className="font-medium">
                            {hasUsableValue(property.coveredarea_admin)
                              ? `${formatTwoDecimals(property.coveredarea_admin) || property.coveredarea_admin} sq.ft`
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2">
                      <button className="flex-1 py-2 text-sm font-medium text-yellow-700 hover:text-yellow-900 transition-colors">
                        View Details →
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 pt-6 border-t border-gray-200 pb-32">
                <div className="flex flex-col items-center gap-4 w-full md:pr-32">
                  <div className="text-sm text-gray-600 text-center">
                    Showing {(page - 1) * ITEMS_PER_PAGE + 1} to{" "}
                    {Math.min(page * ITEMS_PER_PAGE, properties.length)} of{" "}
                    {properties.length} properties
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Previous
                    </button>
                    <div className="flex items-center justify-center space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`w-10 h-10 flex items-center justify-center text-sm rounded-lg transition-all ${
                            page === p
                              ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md"
                              : "text-gray-700 hover:bg-gray-100 hover:shadow-sm"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                      className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Property Details Modal */}
        {selectedProperty && !showReassessModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col animate-slideUp">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 via-white to-blue-50">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
                    <Building className="h-6 w-6 text-blue-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Property Inspection</h2>
                    <div className="flex items-center mt-1 space-x-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 text-sm font-semibold">
                        <MapPin className="h-3 w-3 mr-1" />
                        PIN: {selectedProperty.pin}
                      </span>
                      {selectedProperty.circle && (
                        <span className="text-gray-600 text-sm flex items-center">
                          <Navigation className="h-3 w-3 mr-1" />
                          Circle: <span className="font-medium ml-1">{selectedProperty.circle}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={closeDetailModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={submitting}
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Left Column - Image and Basic Info */}
                  <div className="lg:col-span-1 space-y-6">
                    {/* Property Image */}
                    <div 
                      className="relative group bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden h-64 flex items-center justify-center shadow-inner cursor-pointer"
                      onClick={() => selectedProperty.image && setShowFullImage(true)}
                    >
                      {selectedProperty.image ? (
                        <>
                          <div 
                            className="w-full h-full transition-transform duration-300 ease-in-out flex items-center justify-center"
                            style={{ transform: `rotate(${rotation}deg)` }}
                          >
                            <img
                              src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                              alt="Property"
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          <div className="absolute bottom-4 right-4 flex gap-2 z-10">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRotate(e);
                              }}
                              className="p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-all shadow-lg"
                              title="Rotate Image"
                            >
                              <ArrowRightLeft className="h-6 w-6" />
                            </button>
                            {rotation !== 0 && (
                              <button
                                onClick={handleSaveRotation}
                                disabled={savingImageRotation}
                                className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                                title="Save Rotation"
                              >
                                {savingImageRotation ? (
                                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <CheckCircle className="h-6 w-6" />
                                )}
                              </button>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="text-center p-6">
                          <Home className="h-20 w-20 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500 font-medium text-lg">No Image Available</p>
                          <p className="text-gray-400 text-sm mt-1">Property visual not captured</p>
                        </div>
                      )}
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <h3 className="font-semibold text-gray-900">Comment Timeline</h3>
                        <div className="text-xs text-gray-500">Chat view</div>
                      </div>

                      {timelineLoading ? (
                        <div className="text-sm text-gray-600">Loading...</div>
                      ) : timelineError ? (
                        <div className="text-sm text-red-600">{timelineError}</div>
                      ) : (
                        <div className="space-y-3">
                          {(() => {
                            // Create additional timeline items for supervisor comments
                            const additionalItems = [];

                            // Add supervisor flag comment
                            if (selectedProperty.supervisor_flag_comment) {
                              additionalItems.push({
                                id: "supervisor-flag-comment",
                                type: "supervisor",
                                user_role: "supervisor",
                                text: selectedProperty.supervisor_flag_comment,
                                user_name: "Supervisor",
                                stage: "Flagged",
                                created_at: selectedProperty.flagged_at || selectedProperty.assigned_at || null,
                                isYellow: true
                              });
                            }

                            // Add latest supervisor assignment comment
                            const latestAssignment = getLatestSupervisorAssignment(selectedProperty);
                            if (latestAssignment?.reason) {
                              additionalItems.push({
                                id: "supervisor-assignment-comment",
                                type: "supervisor",
                                user_role: "supervisor",
                                text: latestAssignment.reason,
                                user_name: "Supervisor",
                                stage: "Assigned",
                                created_at: latestAssignment?.assigned_at || null
                              });
                            }

                            // Combine all items and deduplicate
                            const combinedItems = [...additionalItems, ...(timeline || [])];
                            const seen = new Set();
                            const allItems = combinedItems.filter(item => {
                              // Create a unique key for each item to avoid duplicates
                              let key;
                              if (item.id) {
                                key = item.id;
                              } else if (item.type === "supervisor" && item.isYellow) {
                                key = "supervisor-flag-comment-" + (item.text || "");
                              } else if (item.type === "supervisor") {
                                key = "supervisor-assignment-comment-" + (item.text || "");
                              } else if (item.text) {
                                key = item.type + "-" + item.text + "-" + (item.created_at || "");
                              } else {
                                key = item.action + "-" + (item.created_at || "") + "-" + item.id;
                              }
                              
                              if (seen.has(key)) {
                                return false;
                              }
                              seen.add(key);
                              return true;
                            });

                            if (allItems.length === 0) {
                              return <div className="text-sm text-gray-600">No previous comments found for this property.</div>;
                            }

                            return allItems.map((item) => {
                              const createdAt = item?.created_at ? new Date(item.created_at).toLocaleString() : "";
                              const role = String(item.user_role || "").toLowerCase();
                              const isSupervisor = item.type === "supervisor" || role === "supervisor";
                              const isInspector = item.type === "inspector_explanation" || role === "inspector";
                              const isAction = item.type === "action";

                              const actionText = (() => {
                                if (!isAction) return "";
                                if (String(item.action || "").startsWith("FLAG_PROPERTY")) return "Flagged";
                                if (item.action === "ACCEPT_PROPERTY") return "Resolved";
                                if (item.action === "UNFLAG_PROPERTY") return "Unflagged";
                                return String(item.action || "Action");
                              })();

                              const rows = item?.rows || {};
                              const rowEntries = [
                                ["Floor", rows.floor],
                                ["Land Use", rows.landuse],
                                ["Land Area", rows.landarea],
                                ["Covered Area", rows.coveredarea],
                              ].filter(([, v]) => hasUsableValue(v));

                              if (isAction) {
                                return (
                                  <div key={item.id} className="flex justify-center">
                                    <div className="px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                                      {actionText}
                                      {createdAt ? ` • ${createdAt}` : ""}
                                    </div>
                                  </div>
                                );
                              }

                              const sideClass = isSupervisor ? "justify-start" : "justify-end";
                              const bubbleClass = item.isYellow
                                ? "bg-yellow-50 border-yellow-200 text-yellow-900"
                                : isSupervisor
                                  ? "bg-red-50 border-red-200 text-red-900"
                                  : "bg-blue-50 border-blue-200 text-blue-900";
                              const headerLabel = item.isYellow ? "Supervisor Flag Comment" : isSupervisor ? "Supervisor comment" : isInspector ? "Inspector comment" : "Comment";

                              return (
                                <div key={item.id} className={`flex ${sideClass}`}>
                                  <div className={`max-w-[85%] border rounded-2xl px-4 py-3 shadow-sm ${bubbleClass}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
                                      <div className="text-xs font-semibold">
                                        {headerLabel}
                                        {item.user_name ? ` • ${item.user_name}` : ""}
                                        {item.stage ? ` • ${item.stage}` : ""}
                                      </div>
                                      {createdAt && <div className="text-[11px] opacity-80">{createdAt}</div>}
                                    </div>

                                    {item.type === "inspector_explanation" ? (
                                      <div className="space-y-2">
                                        {hasUsableValue(item.overall) && (
                                          <div className="text-sm whitespace-pre-wrap">{item.overall}</div>
                                        )}
                                        {rowEntries.length > 0 && (
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {rowEntries.map(([k, v]) => (
                                              <div key={k} className="bg-white/70 border border-black/5 rounded-lg p-2">
                                                <div className="text-[11px] font-semibold opacity-80">{k}</div>
                                                <div className="text-sm whitespace-pre-wrap">{v}</div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="text-sm whitespace-pre-wrap">{item.text || ""}</div>
                                    )}
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right Column - Data and Comparison */}
                  <div className="lg:col-span-2 space-y-8">
                    {/* Data Comparison Section */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          <FileText className="h-5 w-5 mr-2 text-blue-600" />
                          Property Information
                        </h3>
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-red-100 border border-red-200 rounded mr-1.5"></div>
                            <span className="text-xs text-gray-500">Difference Flagged</span>
                          </div>
                        </div>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full border rounded-lg overflow-hidden">
                          <thead>
                            <tr className="bg-gray-100 text-sm">
                              <th className="p-3 text-left font-semibold text-gray-700">Feature</th>
                              <th className="p-3 text-left font-semibold text-gray-700">Admin</th>
                              <th className="p-3 text-left font-semibold text-gray-700">ML (Computer Vision Prediction)</th>
                              <th className="p-3 text-left font-semibold text-gray-700">Reassessment</th>
                            </tr>
                          </thead>
                          <tbody className="text-sm">
                            {[
                              { 
                                label: "Floor", 
                                field: "floor",
                                admin: selectedProperty.floor_admin, 
                                ml: getMLDisplayValue(selectedProperty, 'floor'),
                                reassess: selectedProperty.floors_pred
                              },
                              { 
                                label: "Land Use", 
                                field: "landuse",
                                admin: selectedProperty.landuse_admin, 
                                ml: getMLDisplayValue(selectedProperty, 'landuse'),
                                reassess: selectedProperty.land_use_pred
                              },
                              { 
                                label: "Land Area", 
                                field: "landarea",
                                admin: selectedProperty.landarea_admin, 
                                ml: getMLDisplayValue(selectedProperty, 'landarea'),
                                reassess: selectedProperty.landarea_combined_pred,
                                unit: "sq.yards"
                              },
                              { 
                                label: "Covered Area", 
                                field: "coveredarea",
                                admin: selectedProperty.coveredarea_admin, 
                                ml: getMLDisplayValue(selectedProperty, 'coveredarea'),
                                reassess: selectedProperty.coveredarea_combined_pred,
                                unit: "sq.ft"
                              }
                            ].map((row) => {
                              const isDifferent = areValuesDifferent(row.admin, row.ml, row.field);
                              return (
                                <tr key={row.label} className={`border-b last:border-0 ${isDifferent ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                                  <td className="p-3 font-medium text-gray-800">{row.label}</td>
                                  <td className="p-3 text-gray-600">
                                    {row.label === "Land Area" || row.label === "Covered Area"
                                      ? (row.admin
                                          ? `${formatTwoDecimals(row.admin) || row.admin} ${row.unit}`
                                          : "N/A")
                                      : (row.admin || "N/A")}
                                  </td>
                                  <td className={`p-3 font-medium ${isDifferent ? 'text-red-600' : 'text-gray-600'}`}>
                                    {row.ml}
                                  </td>
                                  <td className="p-3 font-semibold text-blue-700">
                                    {row.reassess
                                      ? (row.unit
                                          ? `${formatTwoDecimals(row.reassess) || row.reassess} ${row.unit}`
                                          : (formatTwoDecimals(row.reassess) || row.reassess))
                                      : "N/A"}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="bg-blue-50/30">
                              <td className="p-3 font-medium text-gray-800">PT-5 Serial</td>
                              <td className="p-3 text-gray-600">—</td>
                              <td className="p-3 text-gray-600">—</td>
                              <td className="p-3 font-semibold text-blue-700">{selectedProperty.refno || "N/A"}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                    </div>

                    {/* Comment Section */}
                    <div className="pt-6 border-t border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <MessageSquare className="h-5 w-5 mr-2 text-blue-600" />
                        Inspection Comments
                      </h3>
                      <div className="space-y-4">
                        <textarea
                          rows={3}
                          placeholder="Enter your inspection comments, observations, or additional details..."
                          className="w-full border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          disabled={submitting}
                        />
                        <div className="flex justify-between items-center">
                          <button
                            onClick={handleSaveCommentOnly}
                            disabled={!comment.trim() || submitting}
                            className="px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 hover:from-blue-100 hover:to-blue-200 rounded-lg font-medium flex items-center transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Save className="h-4 w-4 mr-2" />
                            Save Comment Only
                          </button>
                          <p className="text-sm text-gray-500">
                            Comment will be automatically included with your final decision
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-6 border-t border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <AlertCircle className="h-5 w-5 mr-2 text-blue-600" />
                        {hasInspectorFinalDecision(selectedProperty, userId)
                          ? "Edit Final Decision"
                          : "Final Decision"}
                      </h3>
                      {(() => {
                        const hasInspectorDecision = hasInspectorFinalDecision(
                          selectedProperty,
                          userId
                        );
                        const isSupervisorDecisionMade = isSupervisorDecisionLocked(selectedProperty);
                        
                        if (isSupervisorDecisionMade) {
                          return (
                            <div className="text-center py-6">
                              <p className="text-gray-500 text-lg">Supervisor has already made a decision.</p>
                              <p className="text-sm text-gray-400">Cannot edit final decision.</p>
                            </div>
                          );
                        }
                        
                        if (hasInspectorDecision) {
                          return (
                            <div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <button
                                  onClick={() => handleReassessAction('no-reassess')}
                                  disabled={submitting}
                                  className="p-5 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl hover:from-red-100 hover:to-red-200 transition-all shadow-sm group disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <div className="flex items-center">
                                    <div className="p-2 bg-white rounded-lg shadow-sm mr-4">
                                      <XCircle className="h-5 w-5 text-red-600" />
                                    </div>
                                    <div className="text-left">
                                      <h4 className="font-semibold text-red-800 text-lg">Edit: Do Not Reassess</h4>
                                      <p className="text-sm text-red-600 mt-1">
                                        {submitting && actionType === 'no-reassess' ? (
                                          <span className="flex items-center">
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-2"></div>
                                            Processing...
                                          </span>
                                        ) : (
                                          "Change decision - mark as accurate"
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </button>

                                <button
                                  onClick={() => handleReassessAction('reassess')}
                                  disabled={submitting}
                                  className="p-5 bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl hover:from-green-100 hover:to-green-200 transition-all shadow-sm group disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <div className="flex items-center">
                                    <div className="p-2 bg-white rounded-lg shadow-sm mr-4">
                                      <Edit className="h-5 w-5 text-green-600" />
                                    </div>
                                    <div className="text-left">
                                      <h4 className="font-semibold text-green-800 text-lg">Edit: Reassess Property</h4>
                                      <p className="text-sm text-green-600 mt-1">
                                        {submitting && actionType === 'reassess' ? (
                                          <span className="flex items-center">
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600 mr-2"></div>
                                            Processing...
                                          </span>
                                        ) : (
                                          "Change decision - update property features"
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              </div>
                              <p className="text-sm text-gray-500 mt-4 text-center">
                                {comment.trim() 
                                  ? "✅ Your comment will be included with your decision" 
                                  : "💡 Add a comment above or proceed without one"}
                              </p>
                            </div>
                          );
                        }
                        
                        return (
                          <div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <button
                                onClick={() => handleReassessAction('no-reassess')}
                                disabled={submitting}
                                className="p-5 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl hover:from-red-100 hover:to-red-200 transition-all shadow-sm group disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <div className="flex items-center">
                                  <div className="p-2 bg-white rounded-lg shadow-sm mr-4">
                                    <XCircle className="h-5 w-5 text-red-600" />
                                  </div>
                                  <div className="text-left">
                                    <h4 className="font-semibold text-red-800 text-lg">Do Not Reassess</h4>
                                    <p className="text-sm text-red-600 mt-1">
                                      {submitting && actionType === 'no-reassess' ? (
                                        <span className="flex items-center">
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-2"></div>
                                          Processing...
                                        </span>
                                      ) : (
                                        "Mark as accurate - no changes needed"
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </button>

                              <button
                                onClick={() => handleReassessAction('reassess')}
                                disabled={submitting}
                                className="p-5 bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl hover:from-green-100 hover:to-green-200 transition-all shadow-sm group disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <div className="flex items-center">
                                  <div className="p-2 bg-white rounded-lg shadow-sm mr-4">
                                    <Edit className="h-5 w-5 text-green-600" />
                                  </div>
                                  <div className="text-left">
                                    <h4 className="font-semibold text-green-800 text-lg">Reassess Property</h4>
                                    <p className="text-sm text-green-600 mt-1">
                                      {submitting && actionType === 'reassess' ? (
                                        <span className="flex items-center">
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600 mr-2"></div>
                                          Processing...
                                        </span>
                                      ) : (
                                        "Update property features and reassess"
                                      )}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            </div>
                            <p className="text-sm text-gray-500 mt-4 text-center">
                              {comment.trim() 
                                ? "✅ Your comment will be included with your decision" 
                                : "💡 Add a comment above or proceed without one"}
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showNotReassessModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-xl">
              <h2 className="text-xl font-bold mb-4 text-gray-800">Reason for Not Reassessing</h2>
              <textarea
                value={notReassessComment}
                onChange={(e) => setNotReassessComment(e.target.value)}
                rows={5}
                placeholder="Enter reason / comments..."
                className="w-full p-3 border rounded mb-3"
              />
              {!notReassessComment.trim() && (
                <p className="text-red-500 text-sm mb-3">Comment is required</p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleNoReassess}
                  disabled={!notReassessComment.trim() || submitting}
                  className={`flex-1 px-4 py-2 rounded text-white ${
                    notReassessComment.trim()
                      ? "bg-gray-700 hover:bg-gray-800"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  {submitting ? "Saving..." : "Confirm"}
                </button>
                <button
                  onClick={() => {
                    setShowNotReassessModal(false);
                    setActionType(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded"
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reassess Modal */}
        {showReassessModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-slideUp">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 via-white to-green-50">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-green-100 to-green-200 rounded-xl">
                    <Edit className="h-6 w-6 text-green-700" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Update Property Features</h2>
                    <div className="flex items-center mt-1 space-x-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-green-100 to-green-200 text-green-800 text-sm font-semibold">
                        <MapPin className="h-3 w-3 mr-1" />
                        PIN: {selectedProperty.pin}
                      </span>
                      {hasPredictions(selectedProperty) && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 text-sm font-semibold">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          Existing Assesed Found
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowReassessModal(false);
                    setFeatures([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={submitting}
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Features Table */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Property Features Comparison
                    </h3>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600">Admin Value</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-sm text-gray-600">Your Input</span>
                      </div>
                    </div>
                  </div>
                  
                  {hasPredictions(selectedProperty) && (
                    <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-4 mb-6">
                      <div className="flex items-start">
                        <Info className="h-5 w-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-amber-800 font-medium mb-1">
                            Note: This property already has Assesed by You
                          </p>
                          <p className="text-amber-700 text-sm">
                            You can modify the predicted values below based on your inspection. 
                            Fields with differences are highlighted.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                          <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Feature</th>
                          <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Admin Data</th>
                          <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">ML (Computer Vision Prediction)</th>
                          <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Your Updated Value</th>
                          <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Unit</th>
                          <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Explanation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {features.map((feature, index) => (
                          <tr 
                            key={feature.key} 
                            className={`border-b border-gray-200 last:border-0 transition-colors ${
                              feature.changed ? 'bg-gradient-to-r from-green-50 to-green-50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="py-4 px-6">
                              <div className="font-medium text-gray-900 flex items-center">
                                {feature.label}
                                {feature.changed && (
                                  <ArrowRightLeft className="h-3 w-3 text-green-500 ml-2" />
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className={`px-3 py-1.5 rounded-lg ${
                                feature.changed 
                                  ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                  : 'text-gray-600'
                              }`}>
                                {feature.before || "—"}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <div className={`px-3 py-1.5 rounded-lg ${
                                feature.ml && feature.ml !== feature.before
                                  ? 'bg-purple-50 text-purple-800 border border-purple-200'
                                  : 'bg-gray-50 text-gray-700'
                              }`}>
                                {feature.ml || "—"}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              {feature.key === "landuse" ? (
                                <select
                                  value={feature.after}
                                  onChange={(e) => updateFeature(index, 'after', e.target.value)}
                                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all shadow-sm ${
                                    feature.changed 
                                      ? 'border-green-300 bg-green-50 text-green-800' 
                                      : 'border-gray-300'
                                  }`}
                                  disabled={submitting}
                                >
                                  <option value="">Select Land Use</option>
                                  <option value="Residential">Residential</option>
                                  <option value="Mixed">Mixed</option>
                                  <option value="Commercial">Commercial</option>
                                </select>
                              ) : (feature.key === "landarea" || feature.key === "coveredarea") ? (
                                <div>&nbsp;</div>
                              ) : (
                                <input
                                  type="text"
                                  value={feature.after}
                                  onChange={(e) => updateFeature(index, 'after', e.target.value)}
                                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all shadow-sm ${
                                    feature.changed 
                                      ? 'border-green-300 bg-green-50 text-green-800' 
                                      : 'border-gray-300'
                                  }`}
                                  placeholder="Enter updated value"
                                  disabled={submitting}
                                />
                              )}
                            </td>
                            <td className="py-4 px-6">
                              <div className="text-gray-500 font-medium">{feature.unit}</div>
                            </td>
                            <td className="py-4 px-6">
                              {(feature.key === "landarea" || feature.key === "coveredarea") ? (
                                <div>&nbsp;</div>
                              ) : (
                                <textarea
                                  rows={1}
                                  placeholder="Explanation (optional)"
                                  value={feature.explanation}
                                  onChange={(e) => updateFeature(index, 'explanation', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all shadow-sm resize-none"
                                  disabled={submitting}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Explanation Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Info className="h-5 w-5 mr-2 text-blue-600" />
                    Overall Explanation
                  </h3>
                  <textarea
                    rows={3}
                    placeholder="Provide an overall explanation for the reassessment..."
                    className="w-full border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all shadow-sm resize-none"
                    value={overallExplanation}
                    onChange={(e) => setOverallExplanation(e.target.value)}
                    disabled={submitting}
                  />
                </div>

                {/* Comment Section (Optional) */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <MessageSquare className="h-5 w-5 mr-2 text-blue-600" />
                    Additional Comments (Optional)
                  </h3>
                  <textarea
                    rows={3}
                    placeholder="Any additional comments about the changes..."
                    className="w-full border border-gray-300 rounded-xl p-4 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all shadow-sm"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    disabled={submitting}
                  />
                  <p className="text-sm text-gray-500 mt-2 flex items-center">
                    <Info className="h-3 w-3 mr-1" />
                    Comments will be automatically saved as "(Property reassessed)"
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowReassessModal(false);
                      setFeatures([]);
                    }}
                    disabled={submitting}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-all shadow-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitReassess}
                    disabled={submitting}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 rounded-lg font-medium flex items-center transition-all shadow-md disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Submit Reassessment
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full Screen Image Modal */}
        {showFullImage && selectedProperty?.image && (
          <div 
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fadeIn"
            onClick={() => {
              setShowFullImage(false);
              setRotation(0);
            }}
          >
            <div className="absolute top-6 right-6 flex space-x-4 z-10">
              <button
                onClick={handleRotate}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
                title="Rotate Image"
              >
                <RotateCw className="h-6 w-6 text-white" />
              </button>
              {rotation !== 0 && (
                <button
                  onClick={handleSaveRotation}
                  disabled={savingImageRotation}
                  className="p-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-full text-white transition-all shadow-lg flex items-center gap-2"
                  title="Save Rotation"
                >
                  {savingImageRotation ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <CheckCircle className="h-6 w-6" />
                  )}
                </button>
              )}
              <button
                onClick={() => {
                  setShowFullImage(false);
                  setRotation(0);
                }}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all shadow-lg flex items-center justify-center"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <motion.div 
              initial={{ scale: 0.9, rotate: 0 }}
              animate={{ scale: 1, rotate: rotation }}
              exit={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={appendCacheBuster(resolveImageUrl(selectedProperty.image), selectedProperty._imgBust)}
                alt="Property Full View"
                className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Assigned;
